import { useCallback, useEffect, useState } from 'react';

import Topbar from '../components/layout/Topbar';
import Button from '../components/shared/Button';
import EmptyState from '../components/shared/EmptyState';
import { useToast } from '../components/shared/Toast';
import api from '../services/api';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  read: boolean;
  action_url?: string;
  created_at: string;
}

export default function Notifications(): JSX.Element {
  const toast = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications');
      setNotifications(response.data as Notification[]);
    } catch (_error) {
      toast.error('Bildirimler yuklenemedi');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  const markAsRead = async (id: number) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (_error) {
      toast.error('Bildirim isaretlenemedi');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Tum bildirimler okundu olarak isaretlendi');
    } catch (_error) {
      toast.error('Islem basarisiz');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'project_invite': return '📨';
      case 'task_assigned': return '📋';
      case 'message': return '💬';
      case 'payment': return '💰';
      case 'system': return '⚙️';
      default: return '🔔';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>      <div style={{ flex: 1, overflow: 'auto' }}>
        <Topbar />
        <div style={{ flex: 1, overflow: 'auto', padding: '2rem' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h1>Bildirimler</h1>
              {notifications.some(n => !n.read) && (
                <Button onClick={() => void markAllAsRead()} variant="secondary" size="sm">
                  Tumunu Okundu Isaretle
                </Button>
              )}
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>Yukleniyor...</div>
            ) : notifications.length === 0 ? (
              <EmptyState
                icon="🔔"
                title="Bildirim Yok"
                message="Henuz bildiriminiz bulunmuyor"
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {notifications.map(notif => (
                  <div
                    key={notif.id}
                    className={`notification-item ${!notif.read ? 'unread' : ''}`}
                    onClick={() => !notif.read && void markAsRead(notif.id)}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notif.type)}
                    </div>
                    <div className="notification-content">
                      <h3>{notif.title}</h3>
                      <p>{notif.message}</p>
                      <span className="notification-time">
                        {new Date(notif.created_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    {!notif.read && <div className="notification-badge" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
